/**
 * Deal Analytics Handler
 * Query handler for calculating deal pipeline metrics
 */

import type { ToolExecutionContext } from '../../types';
import { QueryHandlerBase } from '../base';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * Deal analytics handler
 * Calculates pipeline metrics and forecasts
 */
export class DealAnalyticsHandler extends QueryHandlerBase<any> {
  id = 'get_deal_analytics';

  schema = {
    type: 'object',
    properties: {
      company_id: {
        type: 'string',
        description: 'Optional: Filter analytics to a specific company',
      },
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!context?.userToken) {
      throw new Error('User token is required for analytics');
    }

    try {
      const { GetDealssDocument } = await import('@/src/gql/graphql');
      const client = createUserGraphQLClient(context.userToken);

      // Build filter
      const filter = params.company_id ? { company_id: { eq: params.company_id } } : undefined;

      const result = await client.query({
        query: GetDealssDocument,
        variables: {
          first: 1000, // Get all deals for analytics
          filter,
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const deals = data?.dealsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

      // Calculate metrics
      const activeDealCount = deals.filter((d: any) => d.stage !== 'won' && d.stage !== 'lost').length;
      const wonDealCount = deals.filter((d: any) => d.stage === 'won').length;
      const lostDealCount = deals.filter((d: any) => d.stage === 'lost').length;

      const totalPipelineValue = deals
        .filter((d: any) => d.stage !== 'won' && d.stage !== 'lost')
        .reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

      const weightedPipelineValue = deals
        .filter((d: any) => d.stage !== 'won' && d.stage !== 'lost')
        .reduce((sum: number, d: any) => sum + ((d.amount || 0) * (d.close_probability || 50) / 100), 0);

      const committedRevenue = deals
        .filter((d: any) => (d.stage === 'contract' || d.stage === 'negotiation') && d.is_committed)
        .reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

      const recurringMRR = deals
        .filter((d: any) => d.is_recurring && d.stage !== 'lost')
        .reduce((sum: number, d: any) => sum + (d.monthly_amount || 0), 0);

      const dealCountByStage = {
        lead: deals.filter((d: any) => d.stage === 'lead').length,
        qualified: deals.filter((d: any) => d.stage === 'qualified').length,
        discovery: deals.filter((d: any) => d.stage === 'discovery').length,
        proposal: deals.filter((d: any) => d.stage === 'proposal').length,
        negotiation: deals.filter((d: any) => d.stage === 'negotiation').length,
        contract: deals.filter((d: any) => d.stage === 'contract').length,
        won: wonDealCount,
        lost: lostDealCount,
      };

      const dealValueByStage = {
        lead: deals.filter((d: any) => d.stage === 'lead').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        qualified: deals.filter((d: any) => d.stage === 'qualified').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        discovery: deals.filter((d: any) => d.stage === 'discovery').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        proposal: deals.filter((d: any) => d.stage === 'proposal').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        negotiation: deals.filter((d: any) => d.stage === 'negotiation').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        contract: deals.filter((d: any) => d.stage === 'contract').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        won: deals.filter((d: any) => d.stage === 'won').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
        lost: deals.filter((d: any) => d.stage === 'lost').reduce((sum: number, d: any) => sum + (d.amount || 0), 0),
      };

      const closedDeals = wonDealCount + lostDealCount;
      const winRate = closedDeals > 0 ? (wonDealCount / closedDeals) * 100 : 0;

      const averageDealSize = deals.length > 0 ? deals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0) / deals.length : 0;

      // Calculate average sales cycle length
      let totalDays = 0;
      let closedDealCount = 0;
      deals.forEach((d: any) => {
        if ((d.stage === 'won' || d.stage === 'lost') && d.created_at) {
          const createdDate = new Date(d.created_at);
          const closedDate = d.won_at ? new Date(d.won_at) : d.lost_at ? new Date(d.lost_at) : new Date();
          const days = Math.floor((closedDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
          totalDays += days;
          closedDealCount++;
        }
      });
      const averageSalesCycleLength = closedDealCount > 0 ? totalDays / closedDealCount : 0;

      return {
        totalPipelineValue: Math.round(totalPipelineValue * 100) / 100,
        weightedPipelineValue: Math.round(weightedPipelineValue * 100) / 100,
        committedRevenue: Math.round(committedRevenue * 100) / 100,
        recurringMRR: Math.round(recurringMRR * 100) / 100,
        dealCountByStage,
        dealValueByStage: Object.fromEntries(
          Object.entries(dealValueByStage).map(([k, v]) => [k, Math.round((v as number) * 100) / 100])
        ),
        winRate: Math.round(winRate * 100) / 100,
        averageDealSize: Math.round(averageDealSize * 100) / 100,
        averageSalesCycleLength: Math.round(averageSalesCycleLength * 100) / 100,
        totalDeals: deals.length,
        activeDealCount,
        wonDealCount,
        lostDealCount,
      };
    } catch (error) {
      throw new Error(`Analytics calculation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
